import { useEffect, useMemo, useState, Fragment } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { STATE_STYLE, STATE_ORDER } from "@/components/StateBadge";
import { toast } from "sonner";
import {
  Filter, Printer, UserCheck, X, Repeat2, Eye, LayoutGrid,
  MapPin, User as UserIcon, Calendar as CalendarIcon, Wand2, ChevronDown, ChevronRight,
} from "lucide-react";

const GROUP_OPTIONS = [
  { key: "none", label: "Sin agrupar", icon: LayoutGrid },
  { key: "cliente", label: "Cliente", icon: UserIcon },
  { key: "comuna", label: "Comuna", icon: MapPin },
  { key: "dia", label: "Día", icon: CalendarIcon },
];

export default function AdminPedidos() {
  const [orders, setOrders] = useState([]);
  const [state, setState] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [reps, setReps] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [repId, setRepId] = useState("");
  const [groupBy, setGroupBy] = useState("none");
  const [collapsed, setCollapsed] = useState(new Set());
  const [reassignFor, setReassignFor] = useState(null);
  const [reassignRepId, setReassignRepId] = useState("");
  const [reassignMotivo, setReassignMotivo] = useState("");
  const [detailOrder, setDetailOrder] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoResult, setAutoResult] = useState(null);

  const load = () => {
    const q = state ? `?state=${state}` : "";
    api.get(`/orders${q}`).then((r) => setOrders(r.data));
  };
  useEffect(() => { load(); }, [state]);
  useEffect(() => { api.get("/users?role=repartidor").then((r) => setReps(r.data)); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === orders.length) setSelected(new Set());
    else setSelected(new Set(orders.map((o) => o.id)));
  };

  const assign = async () => {
    if (!repId || selected.size === 0) return;
    try {
      await api.post("/orders/assign", { order_ids: [...selected], repartidor_id: repId });
      toast.success(`${selected.size} pedidos asignados`);
      setSelected(new Set()); setAssignOpen(false); setRepId("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const autoAsignar = async () => {
    setAutoLoading(true); setAutoResult(null);
    try {
      const body = selected.size > 0
        ? { order_ids: [...selected], repartidor_id: "auto" }
        : null;
      const r = await api.post("/orders/auto-asignar", body);
      setAutoResult(r.data);
      toast.success(`${r.data.assigned} asignados · ${r.data.unassigned} sin match`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAutoLoading(false); }
  };

  const reasignar = async () => {
    if (!reassignRepId || !reassignFor) return;
    try {
      await api.post(`/orders/${reassignFor.id}/reasignar`, {
        nuevo_repartidor_id: reassignRepId,
        motivo: reassignMotivo || null,
      });
      toast.success("Pedido reasignado");
      setReassignFor(null); setReassignRepId(""); setReassignMotivo("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printLabel = (oid) => {
    const token = localStorage.getItem("voycargo_token");
    fetch(`${API_BASE}/orders/${oid}/label.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => window.open(URL.createObjectURL(b), "_blank"));
  };

  // ====== GROUPING ======
  const groups = useMemo(() => {
    if (groupBy === "none") return null;
    const keyFn = {
      cliente: (o) => o.cliente_name || "Sin cliente",
      comuna: (o) => o?.destinatario?.comuna || "Sin comuna",
      dia: (o) => (o.created_at || "").slice(0, 10) || "Sin fecha",
    }[groupBy];
    const map = new Map();
    for (const o of orders) {
      const k = keyFn(o);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(o);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [orders, groupBy]);

  const toggleGroup = (k) => {
    const s = new Set(collapsed);
    if (s.has(k)) s.delete(k); else s.add(k);
    setCollapsed(s);
  };

  // Al cambiar de agrupación, todos abiertos
  useEffect(() => { setCollapsed(new Set()); }, [groupBy]);

  // ====== NON-DELIVERY EXTRACT ======
  const lastNoDeliveryEntry = (o) => {
    const h = (o.history || []).slice().reverse();
    return h.find((e) => e.state === "no_entregado");
  };

  const renderRow = (o) => {
    const nd = o.state === "no_entregado" ? lastNoDeliveryEntry(o) : null;
    return (
      <tr key={o.id} className={`hover:bg-zinc-900/60 ${selected.has(o.id) ? "bg-yellow-300/5" : ""}`} data-testid={`order-row-${o.id}`}>
        <td className="px-3 py-3"><input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} data-testid={`check-${o.id}`} /></td>
        <td className="px-4 py-3 font-mono-tech text-sm">{o.tracking_number}</td>
        <td className="px-4 py-3 text-sm">{o.cliente_name || "-"}</td>
        <td className="px-4 py-3 text-sm">{o.destinatario?.nombre}</td>
        <td className="px-4 py-3 text-xs text-zinc-400 max-w-xs truncate">{o.destinatario?.direccion} · {o.destinatario?.comuna}</td>
        <td className="px-4 py-3 text-sm text-zinc-400">{o.repartidor_name || "-"}</td>
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] font-mono-tech uppercase tracking-wider w-fit ${STATE_STYLE[o.state]?.color}`}>
              <span className="w-1 h-1 rounded-full bg-current" />
              {STATE_STYLE[o.state]?.label}
            </span>
            {nd?.reason && (
              <span className="text-[10px] text-red-400/90 font-mono-tech" title={nd.comentario || ""}>
                ⚠ {nd.reason.slice(0, 26)}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-right">
          <div className="inline-flex items-center gap-1.5">
            <button onClick={() => setDetailOrder(o)} className="w-8 h-8 border border-white/10 hover:border-yellow-300/40 hover:text-yellow-300 inline-flex items-center justify-center" title="Ver detalle" data-testid={`detail-${o.id}`}>
              <Eye className="w-3.5 h-3.5" />
            </button>
            {o.repartidor_id && (
              <button onClick={() => { setReassignFor(o); setReassignRepId(""); setReassignMotivo(""); }} className="w-8 h-8 border border-white/10 hover:border-orange-400/50 hover:text-orange-400 inline-flex items-center justify-center" title="Reasignar" data-testid={`reassign-${o.id}`}>
                <Repeat2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => printLabel(o.id)} className="w-8 h-8 border border-white/10 hover:border-yellow-300/40 hover:text-yellow-300 inline-flex items-center justify-center" title="Etiqueta PDF" data-testid={`print-${o.id}`}>
              <Printer className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Operación</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Todos los pedidos</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="btn-auto-asignar"
            onClick={autoAsignar}
            disabled={autoLoading}
            className="border border-yellow-300/50 text-yellow-300 hover:bg-yellow-300/10 font-bold px-4 py-2.5 uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-50"
            title={selected.size > 0 ? `Auto-asignar ${selected.size} seleccionados` : "Auto-asignar todos los pendientes"}
          >
            <Wand2 className="w-4 h-4" />
            {autoLoading ? "Asignando..." : selected.size > 0 ? `Auto-asignar (${selected.size})` : "Auto-asignar zonas"}
          </button>
          {selected.size > 0 && (
            <button data-testid="open-assign-btn" onClick={() => setAssignOpen(true)} className="bg-yellow-300 text-black font-bold px-5 py-2.5 uppercase tracking-widest text-sm inline-flex items-center gap-2 hover:bg-yellow-200">
              <UserCheck className="w-4 h-4" /> Asignar ({selected.size})
            </button>
          )}
        </div>
      </div>

      {/* Auto result banner + detail */}
      {autoResult && (
        <div className="mb-4 border border-yellow-300/30 bg-yellow-300/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Resultado auto-asignación</div>
            <button onClick={() => setAutoResult(null)} className="text-zinc-400"><X className="w-4 h-4" /></button>
          </div>
          <div className="text-sm mb-3">
            <b className="text-yellow-300">{autoResult.assigned}</b> pedidos asignados por zona ·{" "}
            <b className="text-orange-300">{autoResult.unassigned}</b> sin repartidor en su comuna
          </div>
          {autoResult.detail && autoResult.detail.length > 0 && (
            <div className="max-h-64 overflow-y-auto border border-white/10 divide-y divide-white/5">
              {autoResult.detail.map((d, i) => (
                <div key={i} className="px-3 py-2 flex items-center justify-between text-xs" data-testid={`auto-detail-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono-tech text-yellow-300">{d.tracking}</span>
                    <span className="text-zinc-400">{d.comuna || "sin comuna"}</span>
                  </div>
                  {d.assigned_to ? (
                    <span className="text-emerald-300">→ {d.assigned_to}</span>
                  ) : (
                    <span className="text-orange-400">{d.reason || "sin match"}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {autoResult.load_after && Object.keys(autoResult.load_after).length > 0 && (
            <div className="mt-3 text-[10px] text-zinc-500 font-mono-tech uppercase tracking-widest">
              Carga tras asignación: {Object.entries(autoResult.load_after)
                .map(([id, n]) => {
                  const r = reps.find((x) => x.id === id);
                  return r ? `${r.name}=${n}` : null;
                })
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </div>
      )}

      {/* Filter + Group tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono-tech uppercase tracking-widest"><Filter className="w-3.5 h-3.5" /> Estado:</div>
        <button onClick={() => setState("")} className={`px-3 py-1.5 border text-xs font-mono-tech uppercase tracking-widest ${state === "" ? "border-yellow-300 text-yellow-300 bg-yellow-300/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`} data-testid="filter-all">Todos</button>
        {STATE_ORDER.concat(["no_entregado"]).map((s) => (
          <button key={s} onClick={() => setState(s)} className={`px-3 py-1.5 border text-xs font-mono-tech uppercase tracking-widest ${state === s ? "border-yellow-300 text-yellow-300 bg-yellow-300/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`} data-testid={`filter-${s}`}>
            {STATE_STYLE[s]?.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono-tech uppercase tracking-widest"><LayoutGrid className="w-3.5 h-3.5" /> Agrupar:</div>
        {GROUP_OPTIONS.map((g) => (
          <button key={g.key} onClick={() => setGroupBy(g.key)} className={`px-3 py-1.5 border text-xs font-mono-tech uppercase tracking-widest inline-flex items-center gap-1.5 ${groupBy === g.key ? "border-yellow-300 text-yellow-300 bg-yellow-300/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`} data-testid={`group-${g.key}`}>
            <g.icon className="w-3 h-3" /> {g.label}
          </button>
        ))}
      </div>

      {assignOpen && (
        <div className="mb-6 p-6 border border-yellow-300/40 bg-yellow-300/5 flex items-center gap-4 flex-wrap">
          <div className="font-display font-bold">Asignar {selected.size} pedidos a:</div>
          <select value={repId} onChange={(e) => setRepId(e.target.value)} className="bg-zinc-900 border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm font-mono-tech" data-testid="assign-select">
            <option value="">Selecciona repartidor</option>
            {reps.filter((r) => r.active).map((r) => (
              <option key={r.id} value={r.id}>{r.name} · {r.zonas?.join(", ") || "-"}</option>
            ))}
          </select>
          <button onClick={assign} disabled={!repId} className="bg-yellow-300 text-black font-bold px-4 py-2 uppercase tracking-widest text-sm disabled:opacity-50" data-testid="confirm-assign">Confirmar</button>
          <button onClick={() => setAssignOpen(false)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Table */}
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-zinc-900">
            <tr className="text-left text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500">
              <th className="px-3 py-3 w-8"><input type="checkbox" checked={selected.size === orders.length && orders.length > 0} onChange={toggleAll} data-testid="check-all" /></th>
              <th className="px-4 py-3">Tracking</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Destinatario</th>
              <th className="px-4 py-3">Dirección</th>
              <th className="px-4 py-3">Repartidor</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {groupBy === "none" && orders.map(renderRow)}
            {groups && groups.map(([k, list]) => {
              const isOpen = !collapsed.has(k);
              return (
                <Fragment key={`grp-${k}`}>
                  <tr className="bg-zinc-900/70">
                    <td colSpan={8} className="px-4 py-2">
                      <button onClick={() => toggleGroup(k)} className="w-full flex items-center gap-3 text-left" data-testid={`group-row-${k}`}>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-yellow-300" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                        <span className="font-display font-bold uppercase tracking-tight">{k}</span>
                        <span className="text-xs font-mono-tech text-yellow-300 border border-yellow-300/40 px-2 py-0.5">{list.length}</span>
                      </button>
                    </td>
                  </tr>
                  {isOpen && list.map(renderRow)}
                </Fragment>
              );
            })}
            {orders.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500 font-mono-tech text-sm">Sin pedidos</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Reasignar Modal */}
      {reassignFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setReassignFor(null)}>
          <div className="bg-zinc-950 border border-yellow-300/40 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Reasignar</div>
                <div className="font-display font-black text-2xl tracking-tighter mt-1">{reassignFor.tracking_number}</div>
              </div>
              <button onClick={() => setReassignFor(null)} className="text-zinc-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-sm text-zinc-400 mb-4">
              Actualmente asignado a: <b className="text-white">{reassignFor.repartidor_name}</b>
            </div>
            <label className="block text-xs font-mono-tech uppercase tracking-widest text-zinc-500 mb-1">Nuevo repartidor</label>
            <select value={reassignRepId} onChange={(e) => setReassignRepId(e.target.value)} className="w-full bg-zinc-900 border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm font-mono-tech mb-3" data-testid="reassign-select">
              <option value="">Selecciona repartidor</option>
              {reps.filter((r) => r.active && r.id !== reassignFor.repartidor_id).map((r) => (
                <option key={r.id} value={r.id}>{r.name} · {r.zonas?.join(", ") || "-"}</option>
              ))}
            </select>
            <label className="block text-xs font-mono-tech uppercase tracking-widest text-zinc-500 mb-1">Motivo (opcional)</label>
            <input value={reassignMotivo} onChange={(e) => setReassignMotivo(e.target.value)} className="w-full bg-zinc-900 border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm mb-5" placeholder="Ej: repartidor original enfermo" data-testid="reassign-motivo" />
            <button onClick={reasignar} disabled={!reassignRepId} className="w-full bg-yellow-300 text-black font-bold p-3 uppercase tracking-widest text-sm hover:bg-yellow-200 disabled:opacity-50" data-testid="confirm-reassign">
              Reasignar pedido
            </button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailOrder && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDetailOrder(null)}>
          <div className="bg-zinc-950 border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Pedido</div>
                <div className="font-display font-black text-2xl tracking-tighter mt-1">{detailOrder.tracking_number}</div>
              </div>
              <button onClick={() => setDetailOrder(null)} className="text-zinc-400"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-6">
              <div>
                <div className="text-xs text-zinc-500 font-mono-tech uppercase tracking-widest mb-1">Cliente</div>
                <div>{detailOrder.cliente_name || "-"}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 font-mono-tech uppercase tracking-widest mb-1">Repartidor</div>
                <div>{detailOrder.repartidor_name || "-"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-zinc-500 font-mono-tech uppercase tracking-widest mb-1">Destinatario</div>
                <div className="font-bold">{detailOrder.destinatario?.nombre}</div>
                <div className="text-zinc-400 text-xs">{detailOrder.destinatario?.direccion} · {detailOrder.destinatario?.comuna}</div>
              </div>
            </div>

            {/* NO ENTREGA highlight */}
            {detailOrder.state === "no_entregado" && (() => {
              const nd = lastNoDeliveryEntry(detailOrder);
              if (!nd) return null;
              return (
                <div className="mb-6 border border-red-500/30 bg-red-500/5 p-4">
                  <div className="font-mono-tech text-xs text-red-400 uppercase tracking-widest mb-2">/ Motivo de no entrega</div>
                  <div className="font-display font-bold text-lg">{nd.reason || "-"}</div>
                  {nd.destino_no_entrega && (
                    <div className="text-xs text-zinc-400 mt-1">Destino: <b className="text-white">{nd.destino_no_entrega === "bodega" ? "Regresa a bodega" : "Reintento mañana"}</b></div>
                  )}
                  {nd.comentario && (
                    <div className="mt-3 text-sm bg-zinc-900 border-l-2 border-red-500/60 pl-3 py-2 italic">&ldquo;{nd.comentario}&rdquo;</div>
                  )}
                  <div className="text-[10px] text-zinc-500 mt-2 font-mono-tech">{nd.timestamp?.slice(0, 19)} · {nd.user_name}</div>
                </div>
              );
            })()}

            {/* HISTORY */}
            <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-3">/ Historial</div>
            <ol className="space-y-2 mb-6">
              {(detailOrder.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="border-l-2 border-white/10 pl-4 py-1">
                  <div className="text-xs font-mono-tech uppercase tracking-widest text-yellow-300">{h.state_label || h.state}</div>
                  <div className="text-[11px] text-zinc-500">{h.timestamp?.slice(0, 19).replace("T", " ")} · {h.user_name || "-"} ({h.user_role})</div>
                  {h.reason && <div className="text-xs text-red-400/90 mt-0.5">Motivo: {h.reason}</div>}
                  {h.comentario && <div className="text-xs text-zinc-400 mt-0.5 italic">&ldquo;{h.comentario}&rdquo;</div>}
                  {h.reasignado_a_nombre && (
                    <div className="text-xs text-orange-400 mt-0.5">Reasignado a: {h.reasignado_a_nombre}</div>
                  )}
                </li>
              ))}
            </ol>

            <div className="flex gap-2">
              <button onClick={() => { const o = detailOrder; setDetailOrder(null); setReassignFor(o); }} disabled={!detailOrder.repartidor_id} className="flex-1 border border-orange-400/40 text-orange-400 hover:bg-orange-400/10 font-bold p-3 uppercase tracking-widest text-xs disabled:opacity-30 inline-flex items-center justify-center gap-2" data-testid="detail-reassign">
                <Repeat2 className="w-4 h-4" /> Reasignar
              </button>
              <button onClick={() => printLabel(detailOrder.id)} className="flex-1 border border-white/20 hover:border-yellow-300 text-white font-bold p-3 uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                <Printer className="w-4 h-4" /> Etiqueta
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
